<template>
  <div class="validator">
    <div class="validator-bar">
      <div class="validator-bar-color" :class="passwordStrength.className"></div>
    </div>
    <div class="validator-bar-text">
      <span>{{ passwordStrength.title }}</span>
    </div>

    <ul class="validator-rules">
      <li :class="{ checked: rules.isAtLeast8 }">Минимум 8 символов</li>
      <li :class="{ checked: rules.isOnlyLatinLetters }">Только латинские буквы</li>
      <li :class="{ checked: rules.hasLowercase }">Минимум 1 строчная буква</li>
      <li :class="{ checked: rules.isCapital }">Не менее 1 заглавной буквы</li>
      <li :class="{ checked: rules.hasDigit }">Не менее 1 цифры</li>
      <li :class="{ checked: rules.isSpecial }">Не менее 1 спецсимвола</li>
      <li :class="{ checked: rules.isNoSpaceKey }">Отсутствие пробелов</li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue';

interface Props {
  passwordInput: string;
}

interface PasswordStrength {
  title: string;
  className: string;
}

const props = defineProps<Props>();
const emit = defineEmits(['passwordValidity']);

const password = computed(() => props.passwordInput);

// Правила для проверки пароля
const rules = computed(() => ({
  isAtLeast8: password.value.length >= 8,
  isAtLeast10: password.value.length >= 10,
  isAtLeast12: password.value.length >= 12,
  hasDigit: /\d/.test(password.value),
  hasLowercase: /[a-z]/.test(password.value),
  isOnlyLatinLetters: /^(?!.*[А-Яа-яЁё]).*[A-Za-z]/.test(password.value),
  isSpecial: /[!&^%#$@()/=\\.,|<>+\-{}]/.test(password.value),
  isCapital: /[A-Z]/.test(password.value),
  isNoSpaceKey: !/\s/.test(password.value)
}));

// Проверяем соответствие основным требованиям
const isPassConditions = computed(() => {
  const { hasDigit, hasLowercase, isOnlyLatinLetters, isSpecial, isCapital, isNoSpaceKey } = rules.value;
  return hasDigit && hasLowercase && isOnlyLatinLetters && isSpecial && isCapital && isNoSpaceKey;
});

// Определяем сложность пароля
const passwordStrength = computed<PasswordStrength>(() => {
  const { isAtLeast8, isAtLeast10, isAtLeast12 } = rules.value;

  if (!isAtLeast8 || !isPassConditions.value) return { title: 'Недопустимый пароль', className: 'isShort' };
  if (isAtLeast8 && !isAtLeast10) return { title: 'Слабый пароль', className: 'isWeak' };
  if (isAtLeast10 && !isAtLeast12) return { title: 'Хороший пароль', className: 'isFair' };
  if (isAtLeast12) return { title: 'Надежный пароль', className: 'isExcellent' };

  return { title: '', className: '' };
});

const isValid = computed(() => passwordStrength.value.className !== 'isShort');

watchEffect(() => {
  emit('passwordValidity', isValid.value);
});
</script>


<style scoped lang="scss">
.validator:not(:first-child) {
  margin-top: 12px;
}

.validator-bar {
  background-color: #eaeaea;
  width: 100%;
  margin-top: 12px;
  height: 4px;
  border-radius: 3px;
  overflow: hidden;

  &-color {
    height: 100%;
    transition: 0.5s;
    width: 5%;
    border-radius: 3px;
    background-color: #888888;
  }

  &-text {
    font-size: 12px;
    font-weight: 500;
    line-height: 14.63px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    color: #888888;
    margin-top: 4px;
  }

  .isShort {
    background-color: #ff3b30;
    width: 23%;
  }

  .isWeak {
    background-color: #ff6f20;
    width: 45%;
  }

  .isFair {
    background-color: #30a8ff;
    width: 65%;
  }

  .isExcellent {
    background-color: #30b648;
    width: 100%;
  }
}

.validator-rules {
  column-count: 2;
  column-gap: 8px;
  margin-top: 16px;

  li {
    list-style: none;
    break-inside: avoid;
    margin-bottom: 2px;
    display: flex;
    gap: 2px;
    font-size: 10px;
    font-weight: 400;
    line-height: 12.19px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;

    &::before {
      content: '';
      display: block;
      width: 13px;
      height: 13px;
      background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMiIGhlaWdodD0iMTMiIHZpZXdCb3g9IjAgMCAxMyAxMyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIuNzM5MjcgMTFMMiAxMC4yNjA3TDUuNzYwNzMgNi41TDIgMi43MzkyN0wyLjczOTI3IDJMNi41IDUuNzYwNzNMMTAuMjYwNyAyTDExIDIuNzM5MjdMNy4yMzkyNyA2LjVMMTEgMTAuMjYwN0wxMC4yNjA3IDExTDYuNSA3LjIzOTI3TDIuNzM5MjcgMTFaIiBmaWxsPSIjQkJCQkJCIi8+Cjwvc3ZnPgo=");
    }

    &.checked::before {
      background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMiIGhlaWdodD0iMTMiIHZpZXdCb3g9IjAgMCAxMyAxMyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTQuOTA4NjEgMTAuNDAwMkwxLjIwMzYxIDYuNjk1MThMMS45NTExMyA1Ljk0NzY2TDQuOTA4NjQgOC45MDUxOEwxMS4wNTExIDIuNzYyNjlMMTEuNzk4NiAzLjUxMDE4TDQuOTA4NjEgMTAuNDAwMloiIGZpbGw9IiMzMEI2NDgiLz4KPC9zdmc+Cg==");
    }
  }
}
</style>
